import React from "react";
import { fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import { Download, Bookmark } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const PALETTE = ["#00579e", "#15803d", "#b45309", "#6d28d9", "#be185d", "#0e7490", "#9a3412", "#374151", "#0369a1", "#166534"];

const fmtCell = (v, fmt) => {
  if (v === null || v === undefined) return "—";
  if (fmt === "usd") return fmtUSD(v);
  if (fmt === "pct") return `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  if (fmt === "num") return Number(v).toLocaleString();
  return String(v);
};

function ReportChart({ chart }) {
  if (!chart?.data?.length) return null;
  const h = 240;
  if (chart.type === "donut") {
    const key = chart.series[0];
    return (
      <ResponsiveContainer width="100%" height={h}>
        <PieChart>
          <Pie data={chart.data} dataKey={key} nameKey={chart.x} innerRadius={60} outerRadius={95} paddingAngle={2}>
            {chart.data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmtUSD(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (chart.type === "line") {
    return (
      <ResponsiveContainer width="100%" height={h}>
        <LineChart data={chart.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis dataKey={chart.x} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {chart.series.map((s, i) => (
            <Line key={s} type="monotone" dataKey={s} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={chart.data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
        <XAxis dataKey={chart.x} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtUSD(v, { compact: true })} />
        <Tooltip formatter={(v) => fmtUSD(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {chart.series.map((s, i) => (
          <Bar key={s} dataKey={s} fill={PALETTE[i % PALETTE.length]}
               stackId={chart.type === "stacked_bar" ? "a" : undefined} radius={[2, 2, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ReportView({ data, exportUrl, onSave, controls }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid={`report-view-${data.report_id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h3 className="cs-heading text-base font-semibold" data-testid="report-title">{data.name}</h3>
          <p className="text-[11px] text-muted-foreground max-w-2xl">{data.description}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {controls}
          {onSave && (
            <Button size="sm" variant="outline" onClick={onSave} data-testid="report-save-button"
              className="rounded-full h-8 text-xs">
              <Bookmark className="w-3.5 h-3.5 mr-1" /> Save to My Reports
            </Button>
          )}
          {exportUrl && (
            <a href={exportUrl} data-testid="report-export-csv"
               className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-border text-xs hover:bg-[hsl(var(--cs-tint))]">
              <Download className="w-3 h-3" /> CSV
            </a>
          )}
        </div>
      </div>

      {data.kpis?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-kpis">
          {data.kpis.map((k, i) => (
            <div key={i} className="cs-card p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{k.label}</div>
              <div className="cs-mono text-lg font-semibold mt-1">{fmtCell(k.value, k.fmt)}</div>
            </div>
          ))}
        </div>
      )}

      {data.chart && (
        <div className="cs-card p-4" data-testid="report-chart">
          <ReportChart chart={data.chart} />
        </div>
      )}

      {(data.tables || []).map((t, ti) => (
        <div key={ti} className="cs-card overflow-hidden" data-testid={`report-table-${ti}`}>
          <div className="px-4 py-3 text-xs font-semibold border-b border-border">{t.title}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  {t.columns.map((c) => (
                    <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground ${["usd", "pct", "num"].includes(c.fmt) ? "text-right" : ""}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, ri) => (
                  <tr key={ri} className="border-b border-border last:border-0">
                    {t.columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2 ${["usd", "pct", "num"].includes(c.fmt) ? "text-right cs-mono" : ""} ${c.fmt === "usd" && r[c.key] < 0 ? "text-[#b91c1c]" : ""}`}>
                        {fmtCell(r[c.key], c.fmt)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {data.footnote && <div className="text-[11px] text-muted-foreground border-l-2 border-[#00579e] pl-3">{data.footnote}</div>}
    </div>
  );
}
